/**
 * YOLO11 Pose Detector using TensorFlow.js (WebGL backend)
 *
 * Loads a YOLO11 pose model converted to TensorFlow.js format
 * and detects human poses with 17 COCO keypoints.
 *
 * Output format is compatible with MoveNet (@tensorflow-models/pose-detection)
 * so it can be used as a drop-in replacement.
 *
 * Model output shape: (1, 56, 8400) where:
 *   - 56 = 4 (bbox x,y,w,h) + 1 (confidence) + 51 (17 keypoints * 3)
 *   - 8400 = number of candidate detection boxes
 *
 * Each keypoint has (x, y, confidence) values in the 51-dimensional part.
 */

import * as tf from '@tensorflow/tfjs';

const DEFAULT_INPUT_SIZE = 640;
const DEFAULT_CONFIDENCE_THRESHOLD = 0.25;
const DEFAULT_IOU_THRESHOLD = 0.45;

// COCO keypoint names (17 keypoints)
const KEYPOINT_NAMES = [
  'nose',
  'left_eye',
  'right_eye',
  'left_ear',
  'right_ear',
  'left_shoulder',
  'right_shoulder',
  'left_elbow',
  'right_elbow',
  'left_wrist',
  'right_wrist',
  'left_hip',
  'right_hip',
  'left_knee',
  'right_knee',
  'left_ankle',
  'right_ankle'
];

let tfInitialized = false;

/**
 * Initialize TensorFlow.js with WebGL backend, falling back to CPU if needed
 */
async function initializeTensorFlow() {
  if (tfInitialized) {
    return;
  }

  console.log('[YoloPose] Initializing TensorFlow.js...');

  try {
    console.log('[YoloPose] Attempting WebGL backend...');
    await tf.setBackend('webgl');
    await tf.ready();
    console.log('[YoloPose] TensorFlow.js ready with backend:', tf.getBackend());
  } catch (error) {
    console.warn('[YoloPose] WebGL backend failed, falling back to CPU:', error.message);
    try {
      await tf.setBackend('cpu');
      await tf.ready();
      console.log('[YoloPose] TensorFlow.js ready with CPU backend');
    } catch (cpuError) {
      console.error('[YoloPose] Failed to initialize TensorFlow.js:', cpuError);
      throw cpuError;
    }
  }

  tfInitialized = true;
}

/**
 * TensorFlow.js YOLO11 Pose Detector
 *
 * Provides MoveNet-compatible pose detection using YOLO11 pose model.
 */
export class TfjsYoloPoseDetector {
  constructor(modelPath, options = {}) {
    this.modelPath = modelPath;
    this.inputSize = options.inputSize || DEFAULT_INPUT_SIZE;
    this.confThreshold = options.confThreshold || DEFAULT_CONFIDENCE_THRESHOLD;
    this.iouThreshold = options.iouThreshold || DEFAULT_IOU_THRESHOLD;

    this.model = null;
    this.isInitialized = false;

    // Model version info (populated from manifest during initialization)
    this.modelInfo = {
      modelName: null,
      modelJson: null,
      deployedAt: null
    };

    // Scale info for coordinate conversion
    this._lastScale = null;
  }

  /**
   * Initialize the detector and load the TensorFlow.js model
   * @param {number} timeout - Timeout in milliseconds (default: 30000)
   */
  async initialize(timeout = 30000) {
    if (this.isInitialized) return true;

    console.log(`[YoloPose] Loading YOLO11 pose model: ${this.modelPath}`);

    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error(`Model initialization timed out after ${timeout}ms`)), timeout);
    });

    try {
      await Promise.race([
        this._doInitialize(),
        timeoutPromise
      ]);
      return true;
    } catch (error) {
      console.error('[YoloPose] Failed to load model:', error);
      throw error;
    }
  }

  /**
   * Internal initialization logic
   */
  async _doInitialize() {
    try {
      await initializeTensorFlow();

      // Fetch manifest for content-addressed filenames
      // Try unified manifest first, then fall back to separate TF.js pose manifest
      const modelDir = this.modelPath.replace(/\/[^/]+$/, '');
      let actualModelJsonUrl = this.modelPath;

      try {
        // Try unified manifest first
        let manifest = null;
        const unifiedUrl = `${modelDir}/manifest.detector.json?_=${Date.now()}`;
        const unifiedResp = await fetch(unifiedUrl, { cache: 'no-store' });
        if (unifiedResp.ok) {
          manifest = await unifiedResp.json();
          // Unified manifest uses 'tfjsModel' key
          if (manifest.tfjsModel) {
            actualModelJsonUrl = `${modelDir}/${manifest.tfjsModel}`;
            this.modelInfo = {
              modelName: manifest.modelName || null,
              modelJson: manifest.tfjsModel,
              deployedAt: manifest.deployedAt || null
            };
            console.log(`[YoloPose] Model: ${manifest.modelName || 'unknown'}, deployed: ${manifest.deployedAt || 'unknown'}`);
          }
        } else {
          // Fall back to separate TF.js pose manifest
          const tfjsUrl = `${modelDir}/manifest.yolo.pose.json?_=${Date.now()}`;
          const tfjsResp = await fetch(tfjsUrl, { cache: 'no-store' });
          if (tfjsResp.ok) {
            manifest = await tfjsResp.json();
            if (manifest.modelJson) {
              actualModelJsonUrl = `${modelDir}/${manifest.modelJson}`;
              this.modelInfo = {
                modelName: manifest.modelName || null,
                modelJson: manifest.modelJson,
                deployedAt: manifest.deployedAt || null
              };
              console.log(`[YoloPose] Model: ${manifest.modelName || 'unknown'}, deployed: ${manifest.deployedAt || 'unknown'}`);
            }
          } else {
            console.log('[YoloPose] No manifest found, using default model.json path');
          }
        }

        // Read inputSize from manifest if available
        if (manifest && manifest.inputSize) {
          this.inputSize = manifest.inputSize;
        }
      } catch (e) {
        console.warn('[YoloPose] Could not fetch manifest, using default path');
      }

      console.log(`[YoloPose] Loading model from: ${actualModelJsonUrl}`);

      // Load as GraphModel (converted from SavedModel)
      try {
        this.model = await tf.loadGraphModel(actualModelJsonUrl);
      } catch (e) {
        console.log('[YoloPose] Failed to load as GraphModel, trying LayersModel...');
        this.model = await tf.loadLayersModel(actualModelJsonUrl);
      }

      this.isInitialized = true;
      console.log(`[YoloPose] Model loaded. Input size: ${this.inputSize}x${this.inputSize}`);
      console.log(`[YoloPose] TensorFlow.js backend: ${tf.getBackend()}`);

      // Log model info for debugging
      if (this.model.inputs && this.model.inputs[0]) {
        console.log('[YoloPose] Model input:', {
          name: this.model.inputs[0].name,
          shape: this.model.inputs[0].shape
        });
      }

      return true;
    } catch (error) {
      console.error('[YoloPose] Model load error:', error);
      throw error;
    }
  }

  /**
   * Preprocess image for YOLO inference
   *
   * @param {HTMLVideoElement|HTMLImageElement|HTMLCanvasElement} source
   * @returns {tf.Tensor4D} Preprocessed tensor [1, H, W, 3]
   */
  preprocess(source) {
    const srcWidth = source.videoWidth || source.width;
    const srcHeight = source.videoHeight || source.height;

    // Calculate letterbox scaling
    const scale = Math.min(this.inputSize / srcWidth, this.inputSize / srcHeight);
    const newWidth = Math.round(srcWidth * scale);
    const newHeight = Math.round(srcHeight * scale);
    const offsetX = (this.inputSize - newWidth) / 2;
    const offsetY = (this.inputSize - newHeight) / 2;

    this._lastScale = { scale, offsetX, offsetY, srcWidth, srcHeight };

    const padTop = Math.floor(offsetY);
    const padBottom = Math.ceil(offsetY);
    const padLeft = Math.floor(offsetX);
    const padRight = Math.ceil(offsetX);

    return tf.tidy(() => {
      let tensor = tf.browser.fromPixels(source);
      tensor = tf.image.resizeBilinear(tensor, [newHeight, newWidth]);
      tensor = tf.pad(tensor, [
        [padTop, padBottom],
        [padLeft, padRight],
        [0, 0]
      ], 128); // Gray padding
      tensor = tensor.div(255.0);
      return tensor.expandDims(0); // [1, H, W, 3]
    });
  }

  /**
   * Estimate poses from an image/video element
   * Returns array of poses compatible with MoveNet format
   *
   * @param {HTMLVideoElement|HTMLImageElement|HTMLCanvasElement} source
   * @returns {Array} Array of poses: [{keypoints: [{x, y, score, name}, ...], score}]
   */
  async estimatePoses(source) {
    if (!this.isInitialized) {
      throw new Error('[YoloPose] Detector not initialized. Call initialize() first.');
    }

    try {
      const inputTensor = this.preprocess(source);

      // Debug: log input shape once
      if (!this._loggedInputShape) {
        console.log('[YoloPose] Input tensor shape:', inputTensor.shape);
        this._loggedInputShape = true;
      }

      // Run inference
      const outputTensor = await this.model.predict(inputTensor);

      // Debug: log output shape once
      if (!this._loggedOutputShape) {
        console.log('[YoloPose] Output tensor shape:', outputTensor.shape);
        this._loggedOutputShape = true;
      }

      const output = await outputTensor.data();
      const shape = outputTensor.shape;

      // Clean up
      inputTensor.dispose();
      outputTensor.dispose();

      // Postprocess to get poses
      return this.postprocess(output, shape);
    } catch (error) {
      console.error('[YoloPose] Detection error:', error);
      return [];
    }
  }

  /**
   * Postprocess YOLO pose output to get poses with keypoints
   *
   * Output format: (1, 56, 8400)
   *   - 56 = 4 (bbox) + 1 (conf) + 51 (17 keypoints * 3)
   *   - Layout: [x, y, w, h, conf, kp0_x, kp0_y, kp0_conf, kp1_x, kp1_y, kp1_conf, ...]
   *   - Keypoints are INTERLEAVED: (x, y, conf) for each keypoint
   *
   * @param {Float32Array} data Model output data
   * @param {number[]} shape Output shape
   * @returns {Array} Array of poses
   */
  postprocess(data, shape) {
    const numFeatures = shape[1]; // 56
    const numBoxes = shape[2];    // 8400
    const numKeypoints = 17;

    const { scale, offsetX, offsetY, srcWidth, srcHeight } = this._lastScale;

    // Find detections above threshold
    const detections = [];

    // Debug: log top confidences once
    if (!this._loggedTopConf) {
      const confs = [];
      for (let i = 0; i < Math.min(100, numBoxes); i++) {
        confs.push(data[4 * numBoxes + i]);
      }
      confs.sort((a, b) => b - a);
      console.log('[YoloPose] Top 10 confidences:', confs.slice(0, 10).map(c => c.toFixed(4)));
      this._loggedTopConf = true;
    }

    for (let i = 0; i < numBoxes; i++) {
      // Data is in format [feature][box], so index = feature * numBoxes + box
      const conf = data[4 * numBoxes + i]; // Confidence at index 4

      if (conf < this.confThreshold) continue;

      // Get bbox
      const cx = data[0 * numBoxes + i];
      const cy = data[1 * numBoxes + i];
      const w = data[2 * numBoxes + i];
      const h = data[3 * numBoxes + i];

      // Convert from letterboxed to original image coordinates
      const x1 = (cx - w / 2 - offsetX) / scale;
      const y1 = (cy - h / 2 - offsetY) / scale;
      const x2 = (cx + w / 2 - offsetX) / scale;
      const y2 = (cy + h / 2 - offsetY) / scale;

      const bbox = {
        x: Math.max(0, x1),
        y: Math.max(0, y1),
        width: Math.min(srcWidth, x2) - Math.max(0, x1),
        height: Math.min(srcHeight, y2) - Math.max(0, y1)
      };

      // Extract keypoints - interleaved format: (x, y, conf) for each keypoint
      // Starting at feature index 5: [kp0_x, kp0_y, kp0_conf, kp1_x, kp1_y, kp1_conf, ...]
      const keypoints = [];
      for (let k = 0; k < numKeypoints; k++) {
        const kpBaseIdx = 5 + k * 3; // Each keypoint has 3 values: x, y, conf
        const kpX = data[kpBaseIdx * numBoxes + i];
        const kpY = data[(kpBaseIdx + 1) * numBoxes + i];
        const kpConf = data[(kpBaseIdx + 2) * numBoxes + i];

        // Convert from letterboxed to original coordinates
        const originalX = (kpX - offsetX) / scale;
        const originalY = (kpY - offsetY) / scale;

        keypoints.push({
          x: Math.max(0, Math.min(srcWidth, originalX)),
          y: Math.max(0, Math.min(srcHeight, originalY)),
          score: kpConf,
          name: KEYPOINT_NAMES[k]
        });
      }

      detections.push({
        confidence: conf,
        bbox,
        keypoints
      });
    }

    // Apply NMS to get best detections
    const filteredDetections = this.nms(detections);

    // Debug: log detection count periodically
    if (!this._frameCount) this._frameCount = 0;
    this._frameCount++;
    if (this._frameCount % 30 === 1) {
      console.log(`[YoloPose] Frame ${this._frameCount}: ${detections.length} raw, ${filteredDetections.length} after NMS`);
      if (filteredDetections.length > 0) {
        const pose = filteredDetections[0];
        console.log(`[YoloPose] Best pose conf: ${pose.confidence.toFixed(3)}, bbox:`, pose.bbox);
        const ankle = pose.keypoints.find(kp => kp.name === 'left_ankle');
        if (ankle) console.log(`[YoloPose] Left ankle: x=${ankle.x.toFixed(1)}, y=${ankle.y.toFixed(1)}, score=${ankle.score.toFixed(3)}`);
      }
    }

    // Convert to MoveNet-compatible pose format
    return filteredDetections.map(det => ({
      keypoints: det.keypoints,
      score: det.confidence,
      bbox: det.bbox
    }));
  }

  /**
   * Non-Maximum Suppression
   */
  nms(detections) {
    if (detections.length === 0) return [];

    // Sort by confidence
    detections.sort((a, b) => b.confidence - a.confidence);

    const kept = [];

    while (detections.length > 0) {
      const best = detections.shift();
      kept.push(best);

      // Remove overlapping boxes
      detections = detections.filter(det => {
        return this.iou(best.bbox, det.bbox) < this.iouThreshold;
      });
    }

    return kept;
  }

  /**
   * Calculate Intersection over Union
   */
  iou(box1, box2) {
    const x1 = Math.max(box1.x, box2.x);
    const y1 = Math.max(box1.y, box2.y);
    const x2 = Math.min(box1.x + box1.width, box2.x + box2.width);
    const y2 = Math.min(box1.y + box1.height, box2.y + box2.height);

    const intersection = Math.max(0, x2 - x1) * Math.max(0, y2 - y1);
    const area1 = box1.width * box1.height;
    const area2 = box2.width * box2.height;
    const union = area1 + area2 - intersection;

    return union > 0 ? intersection / union : 0;
  }

  /**
   * Get model version info
   */
  getModelInfo() {
    return this.modelInfo;
  }

  /**
   * Stop the detector and clean up resources
   */
  async stop() {
    if (this.model) {
      this.model.dispose();
      this.model = null;
    }
    this.isInitialized = false;
  }
}

/**
 * Factory function to create YOLO pose detector
 * Compatible with MoveNet createDetector interface
 */
export function createYoloPoseDetector(options = {}) {
  const modelPath = options.modelPath || '/models/pose/model.json';
  return new TfjsYoloPoseDetector(modelPath, {
    inputSize: options.inputSize || 640,
    confThreshold: options.confThreshold || 0.25,
    iouThreshold: options.iouThreshold || 0.45
  });
}

export default TfjsYoloPoseDetector;
